import { useEffect, useMemo, useState } from "react";
import { computeQuota } from "../../lib/quota";
import { EXAM_PROFILES } from "../../data/exam-profiles";
import type { Deck, ExamId } from "../../lib/types";

export interface EditDeckModalProps {
  deck: Deck | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (patch: {
    name: string;
    exam: ExamId;
    deadline?: string;
    deadlineLabel?: string;
    dailyNew: number;
  }) => void;
  onDelete?: (deckId: string) => void;
  learnedCount?: number;
}

const DAILY_OPTIONS = [5, 10, 15, 20, 30, 40];

export function EditDeckModal({
  deck,
  isOpen,
  onClose,
  onSave,
  onDelete,
  learnedCount = 0,
}: EditDeckModalProps) {
  const [name, setName] = useState("");
  const [exam, setExam] = useState<ExamId>("hkdse");
  const [hasDeadline, setHasDeadline] = useState(false);
  const [deadlineDate, setDeadlineDate] = useState("");
  const [deadlineLabel, setDeadlineLabel] = useState("");
  const [dailyNew, setDailyNew] = useState(20);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    if (deck && isOpen) {
      setName(deck.name || "");
      setExam(deck.exam || "hkdse");
      setHasDeadline(Boolean(deck.deadline));
      setDeadlineDate(deck.deadline ? deck.deadline.slice(0, 10) : "");
      setDeadlineLabel(deck.deadlineLabel || "");
      setDailyNew(deck.dailyNew || 20);
      setConfirmDelete(false);
      setErrorMsg("");
    }
  }, [deck, isOpen]);

  const quota = useMemo(() => {
    if (!hasDeadline || !deadlineDate || !deck) return null;
    return computeQuota({
      total: deck.wordIds.length,
      learned: learnedCount,
      deadline: deadlineDate,
      today: todayStr,
    });
  }, [hasDeadline, deadlineDate, deck, learnedCount, todayStr]);

  if (!isOpen || !deck) return null;

  const handleApplyPreset = (preset: "2026_dse" | "2027_dse" | "3m" | "6m" | "1y") => {
    const d = new Date();
    if (preset === "2026_dse") {
      setDeadlineDate("2026-04-08");
      setDeadlineLabel("DSE 2026 考期");
    } else if (preset === "2027_dse") {
      setDeadlineDate("2027-04-08");
      setDeadlineLabel("DSE 2027 考期");
    } else if (preset === "3m") {
      d.setMonth(d.getMonth() + 3);
      setDeadlineDate(d.toISOString().slice(0, 10));
      setDeadlineLabel("3 個月衝刺");
    } else if (preset === "6m") {
      d.setMonth(d.getMonth() + 6);
      setDeadlineDate(d.toISOString().slice(0, 10));
      setDeadlineLabel("半年複習");
    } else if (preset === "1y") {
      d.setFullYear(d.getFullYear() + 1);
      setDeadlineDate(d.toISOString().slice(0, 10));
      setDeadlineLabel("1 年長線");
    }
    setHasDeadline(true);
    setErrorMsg("");
  };

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setErrorMsg("請輸入詞單名稱");
      return;
    }
    if (hasDeadline && !deadlineDate) {
      setErrorMsg("請選擇考試日期，或關閉考期設定");
      return;
    }

    onSave({
      name: trimmedName,
      exam,
      deadline: hasDeadline && deadlineDate ? deadlineDate : undefined,
      deadlineLabel: hasDeadline && deadlineDate ? (deadlineLabel.trim() || undefined) : undefined,
      dailyNew,
    });
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-deck-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-[480px] my-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <div>
            <h2 id="edit-deck-modal-title" className="text-[17px] font-bold text-slate-900 dark:text-slate-100 m-0">
              編輯詞單設定
            </h2>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 m-0 mt-0.5">
              修改詞單名稱、考試目標與倒數日程
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="關閉"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-5 text-[14px]">
          {errorMsg && (
            <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 text-[13px] font-medium">
              ⚠️ {errorMsg}
            </div>
          )}

          {/* 1. Deck Name */}
          <div>
            <label htmlFor="deck-name-input" className="block text-[13px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              詞單名稱
            </label>
            <input
              id="deck-name-input"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errorMsg) setErrorMsg("");
              }}
              placeholder="例如：HKDSE English、核心必考詞..."
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 px-3.5 py-2.5 text-slate-900 dark:text-slate-100 outline-none focus:border-amber-500 focus:bg-white dark:focus:bg-slate-900 transition-all font-medium"
            />
          </div>

          {/* 2. Target Exam */}
          <div>
            <span className="block text-[13px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              目標考試體系
            </span>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(EXAM_PROFILES) as ExamId[]).map((eid) => {
                const profile = EXAM_PROFILES[eid];
                const isSelected = exam === eid;
                return (
                  <button
                    key={eid}
                    type="button"
                    onClick={() => setExam(eid)}
                    className={`flex items-center justify-between p-2.5 rounded-xl border text-left cursor-pointer transition-all ${
                      isSelected
                        ? "border-amber-500 bg-amber-500/10 text-amber-900 dark:text-amber-200 font-bold"
                        : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 text-slate-700 dark:text-slate-300 hover:border-slate-300"
                    }`}
                  >
                    <span className="text-[13px]">{profile.label}</span>
                    {isSelected && <span className="text-amber-600 dark:text-amber-400 text-[12px]">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3. Deadline / Exam Date */}
          <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/40">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[13.5px] font-semibold text-slate-900 dark:text-slate-100">
                  考試日期 / 截止日倒數
                </div>
                <div className="text-[12px] text-slate-500 dark:text-slate-400">
                  啟動後將由演算法自動依剩餘天數計算每日最佳配額
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasDeadline}
                  onChange={(e) => {
                    setHasDeadline(e.target.checked);
                    if (e.target.checked && !deadlineDate) {
                      setDeadlineDate("2027-04-08");
                      setDeadlineLabel("DSE 2027 考期");
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
              </label>
            </div>

            {hasDeadline && (
              <div className="flex flex-col gap-3 pt-3 border-t border-slate-200/80 dark:border-slate-700/80">
                {/* Quick Presets */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[11.5px] text-slate-500 font-medium mr-1">快捷選擇:</span>
                  {[
                    { key: "2026_dse", label: "2026 DSE (4月)" },
                    { key: "2027_dse", label: "2027 DSE (4月)" },
                    { key: "3m", label: "3個月" },
                    { key: "6m", label: "半年" },
                    { key: "1y", label: "1年" },
                  ].map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => handleApplyPreset(p.key as any)}
                      className="px-2 py-1 rounded-md text-[11.5px] font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-amber-500 hover:text-amber-600 transition-colors cursor-pointer"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <label htmlFor="deadline-date-picker" className="block text-[12px] font-medium text-slate-600 dark:text-slate-400 mb-1">
                      考期日期
                    </label>
                    <input
                      id="deadline-date-picker"
                      type="date"
                      value={deadlineDate}
                      onChange={(e) => setDeadlineDate(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-[13px] text-slate-900 dark:text-slate-100 outline-none focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="deadline-label-input" className="block text-[12px] font-medium text-slate-600 dark:text-slate-400 mb-1">
                      考期備註名稱 (選填)
                    </label>
                    <input
                      id="deadline-label-input"
                      type="text"
                      value={deadlineLabel}
                      onChange={(e) => setDeadlineLabel(e.target.value)}
                      placeholder="例如：DSE 2027 考期"
                      className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-[13px] text-slate-900 dark:text-slate-100 outline-none focus:border-amber-500"
                    />
                  </div>
                </div>

                {/* Quota feedback badge */}
                {quota && (
                  <div className="p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 text-[12px] flex items-center justify-between">
                    <div>
                      <span className="font-bold">倒數 {quota.daysLeft} 天</span>
                      <span className="opacity-80"> · 剩 {quota.remaining} 詞</span>
                    </div>
                    <div className="font-bold font-grotesk text-emerald-700 dark:text-emerald-300">
                      每天約需 {quota.newPerDay} 個新詞
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 4. Daily New Goal */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[13px] font-semibold text-slate-700 dark:text-slate-300">
                每日新詞目標 (題/天)
              </span>
              <span className="text-[12px] font-bold font-grotesk text-amber-600 dark:text-amber-400">
                {dailyNew} 詞 / 天
              </span>
            </div>
            <div className="grid grid-cols-6 gap-1.5">
              {DAILY_OPTIONS.map((val) => {
                const isSelected = dailyNew === val;
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setDailyNew(val)}
                    className={`py-2 rounded-lg font-grotesk font-bold text-[13px] border cursor-pointer transition-all ${
                      isSelected
                        ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white shadow-xs"
                        : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-slate-400"
                    }`}
                  >
                    {val}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 5. Danger zone: Delete deck */}
          {onDelete && (
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
              {confirmDelete ? (
                <div className="p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-[13px]">
                  <div className="font-bold text-rose-800 dark:text-rose-200 mb-1">
                    確定要刪除「{deck.name}」嗎？
                  </div>
                  <div className="text-slate-600 dark:text-slate-400 mb-3 text-[12px]">
                    此詞單會被移除，但已學過的單詞進度與測驗記錄會保留在你的帳戶中。
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        onDelete(deck.id);
                        onClose();
                      }}
                      className="px-3.5 py-1.5 rounded-lg bg-rose-600 text-white font-bold text-[12.5px] hover:bg-rose-700 transition-colors cursor-pointer"
                    >
                      確認刪除
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(false)}
                      className="px-3.5 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-[12.5px] hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="text-[12.5px] font-medium text-rose-600 hover:text-rose-700 dark:text-rose-400 hover:underline cursor-pointer bg-transparent border-none p-0 flex items-center gap-1"
                >
                  <span>🗑️ 刪除此詞單...</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold text-[13.5px] hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors cursor-pointer"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-2.5 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-[13.5px] hover:opacity-90 active:scale-98 transition-all cursor-pointer shadow-xs"
          >
            儲存變更
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditDeckModal;
